import React, { useCallback, useMemo, useState } from 'react'
import Table from 'antd/lib/table'
import { generatePath, Link } from 'react-router-dom'

import apiManager from 'helpers/apiManager'
import useQuery from 'hooks/useQuery'
import { dateFormatter } from 'helpers'
import { ROUTES } from 'helpers/constants'

import { Button, EyeIcon, PrimaryButton } from 'components'
import InitiateSessionModal from './InitiateSessionModal'

const columns = [
  {
    title: 'Client Name',
    dataIndex: ['client', 'name'],
    key: 'clientName',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
  },
  {
    title: 'Stage',
    dataIndex: 'stage',
    key: 'stage',
  },
  {
    title: 'Created At',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: text => (text ? dateFormatter.returnYYYYMMDD(text) : ''),
  },
  {
    title: 'Updated At',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: text => (text ? dateFormatter.returnYYYYMMDD(text) : ''),
  },
  {
    title: 'Results Iterations',
    dataIndex: 'iteration',
    key: 'iteration',
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (_, record) => {
      const sessionPath = generatePath(ROUTES.ADMIN_AI_SEARCH_SESSION, { sessionId: record.id })
      return (
        <Link to={sessionPath} className="text-blue-500 hover:text-blue-700">
          <EyeIcon className="w-5 h-5" />
        </Link>
      )
    },
  },
]

const AdminAISearch = () => {
  const [showSessionModal, setShowSessionModal] = useState(false)
  const [isCreatingSession, setIsCreatingSession] = useState(false)

  const { data } = useQuery('admin/search/sessions', () => apiManager.adminGetSearchSessions())
  const { data: clients } = useQuery('admin/search/clients', () =>
    apiManager.adminGetSearchClients()
  )

  const sessions = useMemo(
    () =>
      data?.map(session => ({
        ...session,
        key: session.id,
      })) || [],
    [data]
  )

  const handleOpenSessionModal = useCallback(() => {
    setShowSessionModal(true)
  }, [])

  const handleCloseSessionModal = useCallback(() => {
    setShowSessionModal(false)
  }, [])

  const handleCreateSession = useCallback(async payload => {
    setIsCreatingSession(true)
    try {
      await apiManager.adminCreateSearchSession(payload)
      window.location.reload()
    } catch (error) {
      console.error('Failed to create session:', error)
      setIsCreatingSession(false)
    }
  }, [])

  return (
    <div className="w-full min-h-screen px-4 bg-white py-4 md:px-10">
      <h1 className="text-4xl text-center mb-5">AI Search</h1>
      <div className="flex justify-between mb-4">
        <div className="flex gap-3">
          <Link to={ROUTES.ADMIN_AI_SEARCH_CLIENTS}>
            <Button className="bg-gray-600">View Clients</Button>
          </Link>
          <Link to={ROUTES.ADMIN_AI_SEARCH_PROFILES}>
            <Button className="bg-gray-600">View Profiles</Button>
          </Link>
        </div>
        <PrimaryButton onClick={handleOpenSessionModal}>Initiate Session</PrimaryButton>
      </div>
      <div>
        <Table
          columns={columns}
          dataSource={sessions}
          pagination={{
            defaultPageSize: 10,
          }}
        />
      </div>
      <InitiateSessionModal
        show={showSessionModal}
        clients={clients}
        onClose={handleCloseSessionModal}
        onSave={handleCreateSession}
        isLoading={isCreatingSession}
      />
    </div>
  )
}

export default AdminAISearch
